import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IAppState } from "../../redux/types";
import {
  AppStateUserProfile,
  notificationsSettings,
} from "../../redux/userProfile";
import { allowToSignOut } from "../../redux/userProfile/selectors";
import { Dispatcher } from "../../service/RootDispather";
import { AvatarCurrentUser } from "../common/AvatarUser";
import { OverflowTip } from "../common/Common";
import "./styles.scss";
import {
  allBuildings,
  headerPath,
  organizationReady,
} from "../../redux/organization/selectors";
import { NodeType } from "../../redux/organization";
import { Dialog } from "@material-ui/core";
import { useSwipeable } from "react-swipeable";
import { useHistory } from "react-router-dom";
import LanguageSelect from "./LanguageSelect";
import { langByLocal } from "../../utils/Languages";
import { ViewType } from "../../redux/alerts";
import ChooseView from "./ChooseView";
import NodeSelect from "./NodeSelect";
import SoundManager from "../../utils/SoundManager";
import DeviceInfo from "./DeviceInfo";

enum POPUPS {
  Language,
  NodeSelect,
  ChooseViewNotification,
  ChooseViewNStatus,
  DeviceInfo,
}
const Profile = (): JSX.Element => {
  const rootDispatcher = Dispatcher();
  const { t } = useTranslation("common");

  const { lastName, firstName, notifications, id, language } = useSelector<
    IAppState,
    AppStateUserProfile
  >((state) => state.userProfile);
  const { view } = useSelector((state: IAppState) => state.alerts.settings);
  const { subscribeData } = useSelector(
    (state: IAppState) => state.healthCheck
  );
  const orgLoaded = useSelector(organizationReady);
  const mainLoaded = useSelector(allowToSignOut);
  const isEmptyOrganization = useSelector(allBuildings).length === 0;
  const [popupComponent, setpopupComponent] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const onSignOutHandler = useCallback(() => {
    if (!mainLoaded) {
      console.log("no sign out allowed");
      return;
    }
    rootDispatcher.signOut();
    rootDispatcher.setIsAuthenticated(false);
    SoundManager.instance.stop();
  }, [mainLoaded, rootDispatcher]);

  const [prevNotifications, setPrevNotifications] = useState(notifications);
  const onChangeNotifPush = useCallback(() => {
    setLoading(true);

    const newNotif: notificationsSettings = notifications?.push
      ? { ...notifications, push: false }
      : { ...notifications, push: true };
    rootDispatcher.changeNotification(id, newNotif);
    if (!newNotif.push && subscribeData && subscribeData?.mobileId)
      rootDispatcher.unsubscribeNotification({
        mobileId: subscribeData.mobileId,
      });
    else if (newNotif.push && subscribeData && subscribeData?.mobileId)
      rootDispatcher.subscribeNotification(subscribeData);
  }, [id, rootDispatcher, subscribeData, notifications]);

  useEffect(() => {
    if (
      notifications?.push !== prevNotifications?.push ||
      notifications?.sound !== prevNotifications?.sound ||
      notifications?.vibration !== prevNotifications?.vibration
    ) {
      setPrevNotifications(notifications);
      setLoading(false);
    }
  }, [notifications, subscribeData]);

  const onChangeNotifSound = useCallback(() => {
    setLoading(true);
    rootDispatcher.changeNotification(id, {
      ...notifications,
      sound: !notifications.sound,
    });
  }, [rootDispatcher, notifications, id]);

  const onChangeNotifVibrate = useCallback(() => {
    setLoading(true);
    rootDispatcher.changeNotification(id, {
      ...notifications,
      vibration: !notifications.vibration,
    });
  }, [rootDispatcher, notifications, id]);
  const { i18n } = useTranslation();
  const onSaveNewLang = useCallback(
    (lang: string) => {
      if (language !== lang) {
        rootDispatcher.languageChange(lang);
        const langInner = langByLocal(lang);
        if (langInner) {
          i18n.changeLanguage(langInner.code, () => {
            if (langInner.local === "zh") moment.locale("zh_cn");
            else moment.locale(langInner.local);
          });
        }
      }
      setpopupComponent(undefined);
    },
    [language, i18n, rootDispatcher]
  );
  const onSaveView = useCallback(
    (view, presentation) => {
      rootDispatcher.setMonitorsAlertsSettings({ view, presentation });
      setpopupComponent(undefined);
    },
    [rootDispatcher]
  );

  const userPermArr = useSelector((state: IAppState) => headerPath(state, t));
  const onOpenNodeSelect = useCallback(() => {
    setpopupComponent(POPUPS.NodeSelect);
  }, []);
  const handlers = useSwipeable({
    onSwipedRight: () => {
      if (
        popupComponent !== POPUPS.ChooseViewNStatus &&
        popupComponent !== POPUPS.ChooseViewNotification
      )
        setpopupComponent(undefined);
    },
  });
  const history = useHistory();
  const handlersRoot = useSwipeable({
    onSwipedRight: () => history.push("/monitor"),
  });
  const onShowLangs = useCallback(() => {
    setpopupComponent(POPUPS.Language);
  }, []);

  const onShowDeviceInfo = useCallback(() => {
    setpopupComponent(POPUPS.DeviceInfo);
  }, []);
  const onCloseDeviceInfo = useCallback(() => {
    setpopupComponent(undefined);
  }, []);

  const onChooseView = useCallback((viewType: ViewType) => {
    setpopupComponent(
      viewType === ViewType.Notification
        ? POPUPS.ChooseViewNotification
        : POPUPS.ChooseViewNStatus
    );
  }, []);

  const onSaveNodeId = useCallback(
    (newNodeId: number) => {
      setpopupComponent(undefined);
      rootDispatcher.changeMonitorNode(newNodeId);
    },
    [rootDispatcher]
  );

  return (
    <>
      <div className={`profile-view`} {...handlersRoot}>
        <AvatarCurrentUser includeEditIcon={false} />
        <div style={{ marginTop: "16px" }}>
          <OverflowTip text={[firstName, lastName].join(" ")} />
        </div>
        <div className="flex-between" style={{ marginTop: "16px" }}>
          <div className="text-icons">
            <i className="icon-notifications" />
            <span>{t("labels.notifications")}</span>
          </div>

          <div className="switch">
            <input
              type="checkbox"
              name=""
              id="heart-rate-measurement"
              checked={notifications?.push || false}
              onChange={onChangeNotifPush}
              disabled={loading}
            />
            <label htmlFor="heart-rate-measurement">
              {loading ? (
                <span
                  className={`loader-overlay ${
                    !notifications?.push ? "loader-off" : ""
                  }`}
                ></span>
              ) : (
                ""
              )}
            </label>
          </div>
        </div>
        <div
          className={`checkbox checkbox-sm check-text flex-row `}
          style={{ marginTop: "16px" }}
        >
          <input
            type="checkbox"
            id={"checkbox-Sound"}
            checked={notifications?.sound || false}
            onChange={onChangeNotifSound}
            // disabled={!notifications?.push}
            disabled={loading}
            className="checkbox-profile"
          />
          <label htmlFor={"checkbox-Sound"}>
            <span>{t("labels.Sound")}</span>
          </label>
        </div>
        <div
          className={`checkbox checkbox-sm check-text flex-row`}
          style={{ marginTop: "16px" }}
        >
          <input
            type="checkbox"
            id={"checkbox-Vibra"}
            checked={notifications?.vibration || false}
            onChange={onChangeNotifVibrate}
            // disabled={!notifications?.push}
            disabled={loading}
            className="checkbox-profile"
          />
          <label htmlFor={"checkbox-Vibra"}>
            <span>{t("labels.Vibration")}</span>
          </label>
        </div>
        <div className="separator" style={{ marginTop: "16px" }}></div>
        <div
          className="flex-between"
          onClick={onShowLangs}
          style={{ marginTop: "16px", marginBottom: "16px" }}
        >
          <div className="text-icons">
            <i className="icon-globe" />
            <p>{t("labels.Language")}</p>
          </div>
          {language.toUpperCase()}
        </div>
        <div className="separator"></div>
        {!isEmptyOrganization && orgLoaded && (
          <div
            className="breadcrumbs"
            onClick={onOpenNodeSelect}
            style={{ marginTop: "16px", marginBottom: "16px" }}
          >
            {userPermArr.map((el, index) => {
              return (
                <div key={index} className="text-icons">
                  {el.type === NodeType.Building && (
                    <i className="icon-brdc-court" />
                  )}
                  {el.type === NodeType.Department && (
                    <i className="icon-brdc-hospital" />
                  )}
                  {el.type === NodeType.Group && (
                    <i className="icon-brdc-groups" />
                  )}

                  <div className="truncated-text">{el.title}</div>
                </div>
              );
            })}
          </div>
        )}
        <div className="separator"></div>
        <div
          className="text-icons"
          style={{ marginTop: "16px", marginBottom: "16px" }}
        >
          <i className="icon-alerts"></i>
          {t("labels.View")}
        </div>
        <ul className="view-types">
          <li
            className={`${view === ViewType.Notification ? "selected" : ""}`}
            onClick={() => onChooseView(ViewType.Notification)}
            // style={{ justifyContent: "normal" }}
          >
            <div className={"radio"}>
              <input
                type={"radio"}
                id={"Notification"}
                checked={view === ViewType.Notification || false}
                name={"Notification"}
              />
              <label className="md-l-10" htmlFor={"Notification"}>
                <>{t("labels.notifications")}</>
              </label>
            </div>
          </li>
          <li
            className={`${view === ViewType.Status ? "selected" : ""}`}
            onClick={() => onChooseView(ViewType.Status)}
            // style={{ justifyContent: "normal" }}
          >
            <div className={"radio"}>
              <input
                type={"radio"}
                id={"Status"}
                checked={view === ViewType.Status || false}
                name={"Status"}
              />
              <label className="md-l-10" htmlFor={"Status"}>
                <>{t("labels.status")}</>
              </label>
            </div>
          </li>
        </ul>
        <button
          className="btn with-icon danger sign-out"
          onClick={onSignOutHandler}
          style={{ marginTop: "40px" }}
        >
          <i className="icon-people-out"></i>
          {t("buttons.SignOut")}
        </button>
        <i
          className={`icon-info icon-big`}
          onClick={onShowDeviceInfo}
          style={{ color: "#48494B", marginTop: "20px" }}
        />
      </div>
      <Dialog
        open={popupComponent !== undefined}
        fullScreen={true}
        onClose={() => setpopupComponent(undefined)}
      >
        <div className="profile-popups" {...handlers}>
          {popupComponent === POPUPS.Language && (
            <LanguageSelect onChange={onSaveNewLang} />
          )}
          {popupComponent === POPUPS.ChooseViewNStatus && (
            <ChooseView onSave={onSaveView} viewType={ViewType.Status} />
          )}
          {popupComponent === POPUPS.ChooseViewNotification && (
            <ChooseView onSave={onSaveView} viewType={ViewType.Notification} />
          )}
          {popupComponent === POPUPS.NodeSelect && (
            <NodeSelect onSave={onSaveNodeId} />
          )}
          {popupComponent === POPUPS.DeviceInfo && (
            <DeviceInfo onClose={onCloseDeviceInfo} />
          )}
        </div>
      </Dialog>
    </>
  );
};

export default React.memo(Profile);
